import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper, 
  Stack, 
  Divider, 
  List, 
  ListItem, 
  ListItemText, 
  Chip, 
  CircularProgress,
  Card,
  CardContent,
  Avatar,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@mui/material';
import MuiGrid from '../components/MuiGrid';
import { supabase } from '../services/supabase';
import { useNavigate } from 'react-router-dom';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import SportsKabaddiIcon from '@mui/icons-material/SportsKabaddi';
import PersonIcon from '@mui/icons-material/Person';
import GamesIcon from '@mui/icons-material/Games';
import { getBrandConfig } from '../config/branding';
import type { Game, GameResult, Player } from '../types/models';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const brandConfig = getBrandConfig();
  const [loading, setLoading] = useState(true);
  const [recentGames, setRecentGames] = useState<Game[]>([]);
  const [news, setNews] = useState<any[]>([]);
  const [stats, setStats] = useState({
    totalGames: 0,
    totalPlayers: 0,
    civilianWins: 0,
    mafiaWins: 0,
    ties: 0
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch recent games
      const { data: gamesData, error: gamesError } = await supabase
        .from('games')
        .select(`
          *,
          league:league_id (
            id,
            name,
            club:club_id (
              id,
              name
            )
          ),
          judge:judge_id (
            id,
            nickname
          )
        `)
        .order('occurred_at', { ascending: false })
        .limit(5);

      if (gamesError) throw gamesError;
      setRecentGames(gamesData || []);

      // Fetch news items
      const { data: newsData, error: newsError } = await supabase
        .from('news')
        .select('*')
        .order('published_at', { ascending: false })
        .limit(10);
        
      if (newsError) throw newsError;
      setNews(newsData || []);

      // Get overall statistics
      const { data: statsData, error: statsError } = await supabase
        .from('games')
        .select('winner');
      
      if (statsError) throw statsError;
      
      const civilianWins = statsData?.filter(game => game.winner === 'CIVILIANS').length || 0;
      const mafiaWins = statsData?.filter(game => game.winner === 'MAFIA').length || 0;
      const ties = statsData?.filter(game => game.winner === 'TIE').length || 0;
      
      // Get player count
      const { count: playerCount, error: playerCountError } = await supabase
        .from('players')
        .select('*', { count: 'exact', head: true });
      
      if (playerCountError) throw playerCountError;
      
      setStats({
        totalGames: statsData?.length || 0,
        totalPlayers: playerCount || 0,
        civilianWins,
        mafiaWins,
        ties
      });
      
    } catch (error: any) {
      console.error('Error fetching data:', error.message);
    } finally {
      setLoading(false);
    }
  };

  const getWinnerChip = (winner: string) => {
    switch (winner) {
      case 'CIVILIANS':
        return <Chip label="CIVILIANS" color="primary" size="small" />;
      case 'MAFIA':
        return <Chip label="MAFIA" color="error" size="small" />;
      case 'TIE':
        return <Chip label="TIE" color="default" size="small" />;
      default:
        return null;
    }
  };

  const formatDateWithTime = (dateString: string) => {
    try {
      const date = new Date(dateString);
      return `${date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} ${date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
    } catch (error) {
      return dateString;
    }
  };
  
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  // Function to convert plain text URLs to clickable links
  const linkifyText = (text: string) => {
    const urlRegex = /(https?:\/\/[^\s]+)/g;
    return text.split(urlRegex).map((part, i) => {
      if (part.match(urlRegex)) {
        return (
          <a 
            key={i} 
            href={part} 
            target="_blank" 
            rel="noopener noreferrer"
            style={{ 
              color: '#90caf9', 
              textDecoration: 'none',
              borderBottom: '1px dotted #90caf9',
              transition: 'color 0.2s ease, borderBottom 0.2s ease',
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.color = '#bbdefb';
              e.currentTarget.style.borderBottom = '1px solid #bbdefb';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.color = '#90caf9';
              e.currentTarget.style.borderBottom = '1px dotted #90caf9';
            }}
          >
            {part}
          </a>
        );
      }
      return part;
    });
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Welcome to {brandConfig.name}
        </Typography>
        <Typography variant="subtitle1" gutterBottom sx={{ marginBottom: "20px" }}>
          Track Mafia game stats and results {brandConfig.name.includes('Israel') ? 'across Israeli leagues' : 'in our leagues'}
        </Typography>

        
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {/* Stats Summary */}
            <MuiGrid container spacing={3} sx={{ mt: 1, mb: 4 }}>
              <MuiGrid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  height: '100%', 
                  background: 'linear-gradient(145deg, rgba(92, 107, 192, 0.15), rgba(92, 107, 192, 0.3))',
                  color: 'white',
                  border: '1px solid rgba(92, 107, 192, 0.2)'
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                        <GamesIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h4" component="div">
                          {stats.totalGames}
                        </Typography>
                        <Typography variant="body2">
                          Total Games
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </MuiGrid>
              
              <MuiGrid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  height: '100%', 
                  background: 'linear-gradient(145deg, rgba(255, 193, 7, 0.15), rgba(255, 193, 7, 0.3))',
                  color: 'white',
                  border: '1px solid rgba(255, 193, 7, 0.2)'
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: 'warning.main', mr: 2 }}>
                        <PersonIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h4" component="div">
                          {stats.totalPlayers}
                        </Typography>
                        <Typography variant="body2">
                          Active Players
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </MuiGrid>
              
              <MuiGrid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  height: '100%', 
                  background: 'linear-gradient(145deg, rgba(76, 175, 80, 0.15), rgba(76, 175, 80, 0.3))',
                  color: 'white',
                  border: '1px solid rgba(76, 175, 80, 0.2)'
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: 'success.main', mr: 2 }}>
                        <EmojiEventsIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h4" component="div">
                          {stats.civilianWins}
                        </Typography>
                        <Typography variant="body2">
                          Civilian Wins
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </MuiGrid>
              
              <MuiGrid item xs={12} sm={6} md={3}>
                <Card sx={{ 
                  height: '100%', 
                  background: 'linear-gradient(145deg, rgba(244, 67, 54, 0.15), rgba(244, 67, 54, 0.3))',
                  color: 'white',
                  border: '1px solid rgba(244, 67, 54, 0.2)'
                }}>
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Avatar sx={{ bgcolor: 'error.main', mr: 2 }}>
                        <SportsKabaddiIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="h4" component="div">
                          {stats.mafiaWins}
                        </Typography>
                        <Typography variant="body2">
                          Mafia Wins
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </MuiGrid>
            </MuiGrid>
            
            <Stack 
              direction={{ xs: 'column', md: 'row' }} 
              spacing={3} 
              sx={{ mt: 3 }}
            >
              {/* Recent Games */}
              <Paper sx={{ 
                p: 4, 
                flex: 1, 
                borderTop: '3px solid rgba(244, 67, 54, 0.5)',
                backgroundColor: 'rgba(17, 15, 30, 0.6)',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                borderRadius: '16px'
              }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Recent Games
                </Typography>
                
                {recentGames.length > 0 ? (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Game</TableCell>
                          <TableCell>League</TableCell>
                          <TableCell>Date</TableCell>
                          <TableCell>Winner</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {recentGames.map((game) => (
                          <TableRow 
                            key={game.id} 
                            hover
                            onClick={() => navigate(`/games?game=${game.id}`)}
                            sx={{ 
                              cursor: 'pointer',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.08)'
                              }
                            }}
                          >
                            <TableCell>
                              Session {game.session_num}, Game {game.number_in_session}
                            </TableCell>
                            <TableCell>
                              <Box 
                                onClick={(e) => {
                                  e.stopPropagation(); // Prevent row click event
                                  navigate(`/league/${game.league_id}`);
                                }}
                                sx={{ 
                                  cursor: 'pointer',
                                  '&:hover': { textDecoration: 'underline' }
                                }}
                              >
                                {(game.league as any)?.name || 'Unknown'}
                              </Box>
                            </TableCell>
                            <TableCell>
                              {formatDate(game.occurred_at)}
                            </TableCell>
                            <TableCell>
                              {getWinnerChip(game.winner)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No games found. Start playing to see results here!
                  </Typography>
                )}
              </Paper>
              
              {/* News */}
              <Paper sx={{ 
                p: 4, 
                flex: 1, 
                borderTop: '3px solid rgba(92, 107, 192, 0.5)',
                backgroundColor: 'rgba(17, 15, 30, 0.6)',
                backdropFilter: 'blur(8px)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.05)',
                borderRadius: '16px'
              }}>
                <Typography variant="h6" gutterBottom color="primary">
                  News & Updates
                </Typography>
                
                {news.length > 0 ? (
                  <List>
                    {news.map((item, index) => (
                      <React.Fragment key={item.id}>
                        <ListItem
                          sx={{ 
                            display: 'block',
                            py: 1.5
                          }}
                        >
                          <Box sx={{ mb: 1 }}>
                            <Typography 
                              variant="body2" 
                              color="text.secondary"
                              sx={{ mb: 0.5, fontSize: '0.8rem' }}
                            >
                              {formatDateWithTime(item.published_at)}
                            </Typography>
                            <Typography variant="body1">
                              {linkifyText(item.message)}
                            </Typography>
                          </Box>
                        </ListItem>
                        {index < news.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No news available yet.
                  </Typography>
                )}
              </Paper>
            </Stack>
          </>
        )}
      </Box>
    </Container>
  );
};

export default Home;